import type { MealPlanDto } from '@fewerbags/schema';
import { planKindLabel } from '../../lib/plans';
import {
  sortWarnings,
  WARNING_LABEL,
  WARNING_TONE,
  worstTone,
  type WarningTone,
} from '../../lib/profiles';

/** "Dining out" / "Packed · School" badge (D32); nothing for an ordinary meal. */
export function PlanKindBadge({
  plan,
}: {
  plan: Pick<MealPlanDto, 'kind' | 'placeName' | 'dineOutNote'>;
}) {
  const label = planKindLabel(plan);
  if (!label) return null;
  return (
    <span className="rounded-full bg-cream px-3 py-1 text-sm font-semibold text-ink">{label}</span>
  );
}

/** Who eats this meal (read-only chips); "Everyone" when the plan names nobody. */
export function AttendeeChips({ plan }: { plan: Pick<MealPlanDto, 'attendees'> }) {
  if (plan.attendees.length === 0) {
    return (
      <span className="rounded-full bg-white/15 px-3 py-1 text-sm text-white">Everyone eats</span>
    );
  }
  return (
    <ul aria-label="Who eats" className="flex flex-wrap gap-1.5">
      {plan.attendees.map((a, i) => (
        <li
          key={a.personId ?? `guest-${i}`}
          className="rounded-full bg-white/15 px-3 py-1 text-sm text-white"
        >
          {a.name}
          {a.guest ? <span className="text-white/75"> (guest)</span> : null}
        </li>
      ))}
    </ul>
  );
}

const TONE_BOX: Record<WarningTone, string> = {
  error: 'border-fail/40 bg-red-50 text-fail',
  warning: 'border-warn/40 bg-amber-50 text-warn',
  info: 'border-line bg-surface text-ink',
};
const TONE_CHIP: Record<WarningTone, string> = {
  error: 'bg-fail text-white',
  warning: 'bg-warn text-white',
  info: 'bg-brand-soft text-brand',
};
const TONE_ICON: Record<WarningTone, string> = { error: '⛔', warning: '⚠', info: 'ℹ' };

/**
 * Allergen, place-ban, diet and dislike warnings computed for the attendees,
 * most serious first: allergies and place bans in the error tone, diets as
 * warnings, dislikes as information.
 */
export function PlanWarnings({ plan }: { plan: Pick<MealPlanDto, 'warnings'> }) {
  const tone = worstTone(plan.warnings);
  if (!tone) return null;
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={`rounded-2xl border px-4 py-3 text-sm ${TONE_BOX[tone]}`}
    >
      <p className="font-semibold">Heads up for this meal</p>
      <ul className="mt-1 flex flex-col gap-1.5">
        {sortWarnings(plan.warnings).map((w, i) => {
          const t = WARNING_TONE[w.kind];
          return (
            <li
              key={`${w.kind}-${w.recipeId ?? ''}-${w.personId ?? ''}-${w.ingredientId ?? ''}-${i}`}
              className="flex items-start gap-2 text-ink"
            >
              <span
                className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${TONE_CHIP[t]}`}
              >
                <span aria-hidden>{TONE_ICON[t]}</span>
                {WARNING_LABEL[w.kind]}
              </span>
              <span>{w.text}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
